"use client";

import {
  Form,
  FormCheckbox,
  FormInput,
  FormStatus,
  useFormStatus,
} from "@/components/primitives/Form";
import { Button } from "@/components/primitives/Button";
import { ConsentPrivacyLabel } from "@/components/forms/ConsentPrivacyLabel";
import { formatWaitlistPosition, getFormsCopy } from "@/content/forms";
import { joinWaitlist } from "@/lib/api/endpoints";
import { buildFormConsentRecord } from "@/lib/consent";
import { resolveEarlyAccessSource } from "@/lib/early-access";
import type { Locale } from "@/lib/i18n/config";
import { useLocale } from "@/lib/i18n/provider";
import { useLandingConfig } from "@/components/landing/LandingConfigProvider";
import { waitlistInputSchema } from "@/lib/api/types";
import { withAttribution } from "@/lib/utm";

type Props = {
  source?: string;
  className?: string;
  layout?: "stacked" | "inline";
  waitlistPlatform?: "ios" | "android";
  waitlistCountry?: string | null;
};

/** The single waitlist component used across all early-access entry points. */
export function WaitlistForm({
  source,
  className = "",
  layout = "stacked",
  waitlistPlatform,
  waitlistCountry,
}: Props) {
  const { locale } = useLocale();
  const forms = getFormsCopy(locale);
  const { variant: landingVariant } = useLandingConfig();

  return (
    <Form
      schema={waitlistInputSchema}
      trackingName="waitlist"
      successMessage={forms.waitlist.successMessage}
      className={className}
      onSubmit={async (values, signal) => {
        const { consentGiven, ...input } = values;
        if (!consentGiven) return;

        const consentRecord = buildFormConsentRecord({
          consentText: forms.waitlist.consentTextRecord,
          locale,
          formSource: "waitlist",
        });

        const payload = withAttribution(
          { ...input, locale },
          {
            sourceBase: resolveEarlyAccessSource(source),
            landingVariant,
            waitlistPlatform,
            waitlistCountry: waitlistCountry ?? undefined,
          },
        );
        const res = await joinWaitlist(payload, consentRecord, signal);
        if (res.position) {
          const line = formatWaitlistPosition(locale, res.position);
          if (res.confirmationRequired) {
            return `${line} ${forms.waitlist.confirmationEmailNotice}`;
          }
          return line;
        }

        if (res.confirmationRequired) {
          return forms.waitlist.confirmationEmailNotice;
        }
      }}
    >
      {layout === "inline" ? (
        <InlineLayout locale={locale} />
      ) : (
        <StackedLayout locale={locale} />
      )}
    </Form>
  );
}

function StackedLayout({ locale }: { locale: Locale }) {
  const forms = getFormsCopy(locale);

  return (
    <div className="flex flex-col gap-3">
      <FormInput
        name="email"
        type="email"
        label={forms.waitlist.emailLabel}
        placeholder={forms.waitlist.emailPlaceholder}
        required
        autoComplete="email"
      />
      <FormInput
        name="name"
        label={forms.waitlist.nameLabel}
        placeholder={forms.waitlist.namePlaceholder}
        autoComplete="given-name"
      />
      <FormCheckbox
        name="consentGiven"
        required
        label={<ConsentPrivacyLabel text={forms.waitlist.consentLabel} />}
      />
      <SubmitButton
        label={forms.waitlist.stackedSubmitLabel}
        busyLabel={forms.waitlist.busyLabel}
      />
      <FormStatus />
    </div>
  );
}

function InlineLayout({ locale }: { locale: Locale }) {
  const forms = getFormsCopy(locale);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <FormInput
          name="email"
          type="email"
          label={forms.waitlist.emailLabel}
          placeholder={forms.waitlist.emailPlaceholder}
          required
          autoComplete="email"
          className="flex-1"
        />
        <SubmitButton
          label={forms.waitlist.inlineSubmitLabel}
          busyLabel={forms.waitlist.busyLabel}
          className="sm:w-auto"
        />
      </div>
      <FormCheckbox
        name="consentGiven"
        required
        label={<ConsentPrivacyLabel text={forms.waitlist.consentLabel} />}
      />
      <FormStatus />
    </div>
  );
}

function SubmitButton({
  label,
  busyLabel,
  className = "",
}: {
  label: string;
  busyLabel: string;
  className?: string;
}) {
  const status = useFormStatus();
  const busy = status === "submitting";
  return (
    <Button type="submit" variant="primary" size="lg" disabled={busy} className={className}>
      {busy ? busyLabel : label}
    </Button>
  );
}
